import React from "react";

import type { TranscriptBannerKey } from "@performyard/models";
import type {
	VideoConferenceService,
	VideoConferencingConnection,
	VideoConferencingProviderEnum,
} from "@performyard/service-contracts";
import {
	VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES,
	VIDEO_CONFERENCING_PROVIDER_EPHEMERAL_FLAGS,
} from "@performyard/service-contracts";

import { TeamsIcon } from "./TeamsIcon";
import { ZoomIcon } from "./ZoomIcon";

type VideoConferencingProviderUiConfig = {
	/** Full display name for connection-management UI, e.g. "Microsoft Teams" (single source: `@performyard/models`, via service-contracts). */
	longName: string;
	/** Compact display name used everywhere else, e.g. "Teams". */
	shortName: string;
	/** Provider logomark; consumers pick the size. */
	Logo: React.ComponentType<{ size?: number }>;
	/** Ephemeral FF gating this provider while in development (single source: `@performyard/models`). */
	ephemeralFeatureFlag: (typeof VIDEO_CONFERENCING_PROVIDER_EPHEMERAL_FLAGS)[VideoConferencingProviderEnum];
	/** Provider-specific copy for a failed OAuth connect; falls back to a generic message when absent. */
	connectFailureMessage?: string;
	/** Copy for a connect refused because the account lacks the provider's required license. */
	connectUnlicensedMessage?: string;
	/**
	 * Why automatic transcripts can't work for this connection, or null when they can (or the
	 * provider doesn't report settings). Receives the full connection union and narrows to its own
	 * provider's variant, so shared components never branch on provider identity.
	 */
	getTranscriptsUnavailableMessage?: (connection: VideoConferencingConnection) => string | null;
	/**
	 * Banner key shown when this meeting's video conference can never auto-retrieve a transcript
	 * (e.g. a Zoom PMI meeting), or null when retrieval isn't blocked. Narrows to its own provider's
	 * variant; absent when the provider has no such blocked state.
	 */
	getTranscriptBlockedBannerKey?: (videoConference: VideoConferenceService) => TranscriptBannerKey | null;
	/**
	 * Whether a transcript should arrive on its own for this meeting, judged from the effective
	 * settings snapshotted at create. FE analog of the backend registry's policy, but stricter:
	 * it also suppresses when the audio-transcript snapshot is off, and leaves the PMI case to
	 * `getTranscriptBlockedBannerKey` (checked earlier in the banner precedence).
	 */
	expectsAutomaticTranscript: (videoConference: VideoConferenceService) => boolean;
	/**
	 * Promote "scheduled" to "awaiting" once the meeting's scheduled end passes. For providers whose
	 * first webhook is the transcript itself (no meeting-ended event), the scheduled end is the only
	 * signal the meeting is over; absent for providers whose own webhook drives `awaiting`.
	 */
	promotesScheduledToAwaitingAfterEnd?: boolean;
	/**
	 * How long past the meeting's scheduled end to keep promising a transcript before treating it as
	 * overdue (the "delayed" state, which offers manual retrieval). Computed as
	 * `max(durationMultiple × scheduled duration, minimumMinutes)`, which covers both shapes: Zoom's
	 * turnaround scales with meeting length, Teams' is a flat indexing lag.
	 *
	 * These are tuned estimates from vendor guidance, not published SLAs — adjust from real data.
	 */
	transcriptDelay: { durationMultiple: number; minimumMinutes: number };
};

/**
 * Per-provider client-side config — the single place for provider-specific UI data so components
 * never branch on the provider identifier. Keyed on the server enum, so adding a provider there
 * turns this map into a compile-time TODO.
 */
export const VIDEO_CONFERENCING_PROVIDER_CONFIG: Record<
	VideoConferencingProviderEnum,
	VideoConferencingProviderUiConfig
> = {
	zoom: {
		longName: VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES.zoom.long,
		shortName: VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES.zoom.short,
		Logo: ZoomIcon,
		ephemeralFeatureFlag: VIDEO_CONFERENCING_PROVIDER_EPHEMERAL_FLAGS.zoom,
		getTranscriptsUnavailableMessage: (connection) => {
			if (connection.provider !== "zoom" || connection.userSettings == null) {
				return null;
			}
			// An unlicensed (free) Zoom plan has no cloud recording at all, so it outranks the
			// settings-based gates below — those settings can't even be enabled without a paid plan.
			const { cloudRecording, recordingAudioTranscript, licensed } = connection.userSettings;
			if (!licensed) {
				return "Zoom is connected, but automatic transcripts require cloud recording, which isn't included in your current Zoom plan. Upgrade your Zoom plan to enable them, then check again.";
			}
			if (!cloudRecording && !recordingAudioTranscript) {
				return "Zoom is connected, but transcripts can't be retrieved automatically because cloud recording and audio transcripts aren't both enabled for your account. Enable them in your Zoom account settings, then check again.";
			}
			if (!cloudRecording) {
				return "Zoom is connected, but transcripts can't be retrieved automatically because cloud recording is turned off for your account. Enable cloud recording in your Zoom account settings, then check again.";
			}
			if (!recordingAudioTranscript) {
				return "Zoom is connected, but transcripts can't be retrieved automatically because audio transcripts aren't enabled. Turn on audio transcripts in your Zoom account settings, then check again.";
			}
			return null;
		},
		// A PMI meeting never auto-retrieves: its numeric id is shared across the host's PMI meetings,
		// so an incoming transcript can't be attributed to this one meeting.
		getTranscriptBlockedBannerKey: (videoConference) =>
			videoConference.provider === "zoom" && videoConference.settings.usePmi ? "pmi" : null,
		expectsAutomaticTranscript: (videoConference) =>
			videoConference.provider === "zoom" &&
			videoConference.settings.autoRecording === "cloud" &&
			videoConference.settings.audioTranscript !== false,
		// Zoom's stated typical turnaround is ~2x the recorded length. The floor keeps a short meeting
		// from looking overdue at 30 minutes when Zoom's queue is merely busy.
		transcriptDelay: { durationMultiple: 2, minimumMinutes: 60 },
	},
	teams: {
		longName: VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES.teams.long,
		shortName: VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES.teams.short,
		Logo: TeamsIcon,
		ephemeralFeatureFlag: VIDEO_CONFERENCING_PROVIDER_EPHEMERAL_FLAGS.teams,
		// Teams' transcript scope is admin-restricted, so missing tenant-admin consent is the most
		// common first-time connect failure — name it instead of a generic retry message.
		connectFailureMessage: "Unable to connect. Ask your Microsoft 365 admin to grant consent.",
		// The backend refuses unlicensed accounts at connect: Graph would create meetings the
		// organizer can never host (no Teams sign-in, participants stuck in the lobby).
		connectUnlicensedMessage: "Unable to connect. No valid Microsoft Teams license.",
		expectsAutomaticTranscript: (videoConference) =>
			videoConference.provider === "teams" && videoConference.settings.recordAutomatically,
		// Teams has no meeting-ended webhook, so the scheduled end is what flips the promise to "awaiting".
		promotesScheduledToAwaitingAfterEnd: true,
		// Teams' lag is a fixed Graph indexing step (~20 min), not proportional to meeting length, so
		// there's no duration component — just a flat window with margin.
		transcriptDelay: { durationMultiple: 0, minimumMinutes: 45 },
	},
};

export const isVideoConferencingProvider = (value: string): value is VideoConferencingProviderEnum =>
	value in VIDEO_CONFERENCING_PROVIDER_CONFIG;
